import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { useController, useFormContext } from 'react-hook-form'

import RadioGroup from '@mui/material/RadioGroup'

import { config } from '../../config'
import { theme } from '../../themes/theme'
import foiAccessMethod, { ACCESS_METHOD_TYPE } from '../../util/foiAccessMethod'
import { FlexColumn, Divider } from '../styles/Containers'
import { RowFormControl, StyledFormControlLabel, StyledRadio } from '../styles/RadioGroup'
import { formSection, formWording } from '../styles/FormStyleConfig'
import { ErrorText } from '../styles/Text'
import { InfoMessage } from '../common/UserMessage'

const Red = styled.span`
  color: ${theme.colors.error};
`

function FOIDocumentAccessForm({
  format = 'default',
  perspective = 'requestor',
  disabled = false,
  disableDigital = false,
  accessMethodConfig = config.foi.request.accessMethod
}) {
  const { formState, control } = useFormContext()
  const { field: accessMethodField } = useController({ control, name: 'accessMethod', defaultValue: '' })
  const { onChange: handleAccessMethodChange, value: selectedAccessMethod } = accessMethodField
  const otherActiveType = foiAccessMethod.getOtherActiveType(accessMethodConfig)

  useEffect(() => {
    if (disableDigital && selectedAccessMethod && foiAccessMethod.isDigitalLike(selectedAccessMethod)) {
      handleAccessMethodChange('')
    }
  }, [disableDigital, handleAccessMethodChange, selectedAccessMethod])

  const error = !!formState.errors.accessMethod
  const helperText = formState.errors?.accessMethod?.message

  const TitleComponent = formSection[format].font.formTitle
  const TextComponent = formSection[format].font.bodyText

  return (
    <FlexColumn gap={formSection[format].gap.div}>
      <TitleComponent>Document Access</TitleComponent>
      {formSection[format].divider && <Divider />}
      {disableDigital && <InfoMessage>To receive an electronic copy, please provide an email address on your request.</InfoMessage>}
      <FlexColumn gap="0">
        <RowFormControl>
          <TextComponent>
            What is {formWording[perspective].possessive} preferred method to access {formWording[perspective].possessive} files?<Red> *</Red>
          </TextComponent>
          <RadioGroup {...accessMethodField} row id="accessMethod" value={selectedAccessMethod || ''}>
            <StyledFormControlLabel
              value={ACCESS_METHOD_TYPE.DIGITAL}
              control={<StyledRadio />}
              label={foiAccessMethod.getLabel(ACCESS_METHOD_TYPE.DIGITAL, accessMethodConfig)}
              format={format}
              disabled={disabled || disableDigital}
            />
            <StyledFormControlLabel
              value={ACCESS_METHOD_TYPE.PHYSICAL}
              control={<StyledRadio />}
              label={foiAccessMethod.getLabel(ACCESS_METHOD_TYPE.PHYSICAL, accessMethodConfig)}
              format={format}
              disabled={disabled}
            />
            {otherActiveType && (
              <StyledFormControlLabel
                value={otherActiveType.type}
                control={<StyledRadio />}
                label={foiAccessMethod.getSelectionLabel(otherActiveType.type, accessMethodConfig)}
                format={format}
                disabled={disabled || (disableDigital && foiAccessMethod.isDigitalLike(otherActiveType.type))}
              />
            )}
          </RadioGroup>
        </RowFormControl>
        {error && <ErrorText role="alert">{helperText}</ErrorText>}
      </FlexColumn>
    </FlexColumn>
  )
}

FOIDocumentAccessForm.propTypes = {
  format: PropTypes.oneOf(['default', 'condensed']),
  perspective: PropTypes.oneOf(['requestor', 'staff']),
  disabled: PropTypes.bool,
  disableDigital: PropTypes.bool,
  accessMethodConfig: PropTypes.shape({
    digitalLabel: PropTypes.string.isRequired,
    physicalLabel: PropTypes.string.isRequired,
    otherActiveType: PropTypes.shape({
      type: PropTypes.oneOf([ACCESS_METHOD_TYPE.OTHER_DIGITAL, ACCESS_METHOD_TYPE.OTHER_PHYSICAL, ACCESS_METHOD_TYPE.OTHER_PICKUP]).isRequired,
      label: PropTypes.string.isRequired,
      pricingDetails: PropTypes.string
    })
  })
}

export default FOIDocumentAccessForm
